import React, { useState } from 'react'
import generatePDF, { Margin } from 'react-to-pdf'
import dayjs from 'dayjs'
import axios from 'axios'
import { toast } from 'react-toastify'
import { getDateOfTherapyReport } from 'src/mentor/components/TherapyReportMakingPage/TherapyReportMakingPageComponents'
import { sleep } from 'src/mentor/helpers/utilityFunctions'
import { postMPDataWithParams, mentorApiURL } from 'src/mentor/services'
import { postSPDataWithParams, supportApiURL } from 'src/supportPortal/services/services'
import { getCountryCode, getMainLanguageCode } from 'src/mentor/services'

export default function useTherapyReportPreviewActions({
  therapyReport,
  therapyReportContainerRef,
  isAlwaysPrinting,
  setIsPrintingPastHistoryReports,
  afterSaveGeneratePdfFunc,
  sendReportToTriggeringPractitionerApiSignature,
  onIssueReport, // Function to save report with status=completed
  bookedEventInfo, // Optional: for generating PDF filename
}) {
  const [isPrintingVar, setIsPrinting] = useState(false)
  const isPrinting = isAlwaysPrinting ? true : isPrintingVar
  const [saving, setSaving] = useState(false)
  const [sendingReport, setSendingReport] = useState(false)
  const [issuingReport, setIssuingReport] = useState(false)
  const [downloadingReport, setDownloadingReport] = useState(false)

  async function printTherapy() {
    // Note: Status check is handled by the parent component (printReport)
    // This function is only called for completed reports, so we can proceed directly
    // If called for draft reports, the parent's printReport will fall back to saveAndPrint
    
    setIsPrinting(true)
    setIsPrintingPastHistoryReports(true)
    await sleep(500)
    
    // Only print the report content, not the drawer
    if (!therapyReportContainerRef?.current) {
      setIsPrintingPastHistoryReports(false)
      setIsPrinting(false)
      alert('Unable to print: Report content not found. Please try refreshing the page.')
      return
    }
    
    let usedNewWindow = false
    let reportElement = null
    
    try {
      // Get the report container element
      reportElement = therapyReportContainerRef.current
      
      if (!reportElement) {
        throw new Error('Report container element not found')
      }
      
      // Clone the element to avoid modifying the original
      let clonedElement
      try {
        clonedElement = reportElement.cloneNode(true)
      } catch (cloneError) {
        throw new Error(`Failed to clone report element: ${cloneError?.message || 'Unknown error'}`)
      }
      
      if (!clonedElement) {
        throw new Error('Failed to clone report element')
      }
      
      // Remove all UI elements from the cloned HTML
      const selectors = [
        '.hide-on-print-only',
        '.therapy-report-draft-watermark',
        '.therapy-report-page-footer', // Remove the floating page footer - we use @page CSS instead
        'button',
        '[class*="MuiButton"]',
        '[class*="MuiIconButton"]',
        '[class*="MuiDrawer"]',
        '[class*="MuiBackdrop"]',
        '[class*="MuiDialog"]',
        '[class*="MuiModal"]',
        '[class*="MuiPaper-root"][class*="MuiDrawer"]',
        '[class*="report-drawer-header"]',
        '[class*="report-drawer-actions"]',
        '[class*="MuiToolbar"]',
        '[class*="MuiAppBar"]',
        'input[type="button"]',
        'input[type="submit"]',
        'a[role="button"]',
        '[data-testid*="button"]',
        '[aria-label*="print"]',
        '[aria-label*="save"]'
      ]
      const elementsToRemove = clonedElement.querySelectorAll(selectors.join(', '))
      elementsToRemove.forEach(el => el.remove())
      
      // Remove floating page number indicator (the "Page 1 of X" text)
      // Look for ALL elements with fixed positioning that might be the page indicator
      const allElements = clonedElement.querySelectorAll('*')
      allElements.forEach(el => {
        const text = (el.textContent || '').trim()
        const style = el.getAttribute('style') || ''
        
        // Check for fixed position
        const isFixed = style.includes('position: fixed') || 
                       style.includes('position:fixed') ||
                       style.includes('position:fixed;') ||
                       style.includes('position: fixed;')
        
        // Check for bottom positioning (common for page indicators)
        const hasBottom = style.includes('bottom:') || 
                         style.includes('bottom :') ||
                         style.match(/bottom\s*:\s*\d+px/i)
        
        // Check for transform (common for centering)
        const hasTransform = style.includes('transform:') || style.includes('transform:')
        
        // Check if text contains "Page" (case insensitive) - including variations like "Page.", "Page 1", etc.
        // Be very permissive - if it starts with "page" or contains "page" followed by anything, remove it
        const hasPageText = /^page/i.test(text.trim()) || 
                           text.toLowerCase().includes('page') ||
                           text.match(/page\s*\.?\s*\d*/i) ||
                           text.match(/page\s*of/i) ||
                           text.match(/^page\.?$/i)
        
        // Remove if it's fixed position with page-related text
        if (isFixed && hasPageText) {
          el.remove()
          return
        }
        
        // Also remove fixed position elements at the bottom (likely page indicators)
        // This catches elements like the page number indicator even if text matching fails
        // Remove ALL fixed position elements at the bottom - they're almost always UI overlays
        if (isFixed && hasBottom) {
          el.remove()
          return
        }
        
        // Remove fixed position elements with transform (centered overlays like page indicators)
        if (isFixed && hasTransform) {
          el.remove()
          return
        }
        
        // Remove any element with z-index > 100 and fixed position that contains "page" text
        if (isFixed && (style.includes('z-index:') || style.includes('zIndex:'))) {
          const zIndexMatch = style.match(/z-index\s*:\s*(\d+)/i) || style.match(/zIndex\s*:\s*(\d+)/i)
          if (zIndexMatch && parseInt(zIndexMatch[1]) > 100) {
            // If it has high z-index and page text, remove it
            if (hasPageText) {
              el.remove()
              return
            }
            // Also remove if it's at the bottom with transform (centered indicator)
            if (hasBottom && hasTransform) {
              el.remove()
              return
            }
          }
        }
      })
      
      // Remove any text or elements containing "reactBusinessPortal" (workspace name)
      const allElementsForCleanup = clonedElement.querySelectorAll('*')
      allElementsForCleanup.forEach(el => {
        const text = (el.textContent || '').trim()
        // Remove elements containing reactBusinessPortal
        if (text.toLowerCase().includes('reactbusinessportal')) {
          el.remove()
          return
        }
        // Also check and clean text nodes
        if (el.childNodes) {
          el.childNodes.forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
              const nodeText = node.textContent || ''
              if (nodeText.toLowerCase().includes('reactbusinessportal')) {
                node.textContent = nodeText.replace(/reactBusinessPortal/gi, 'AUMHUM')
              }
            }
          })
        }
      })
      
      // Remove any elements with "Draft" text or draft-related classes
      const allElementsForDraft = clonedElement.querySelectorAll('*')
      allElementsForDraft.forEach(el => {
        const text = el.textContent || ''
        // Handle className - it can be a string, DOMTokenList, or object
        // Convert to string safely
        let classNameStr = ''
        try {
          if (typeof el.className === 'string') {
            classNameStr = el.className
          } else if (el.className) {
            // Handle DOMTokenList, SVGAnimatedString, or other objects
            classNameStr = String(el.className)
          }
        } catch (e) {
          // If className access fails, just use empty string
          classNameStr = ''
        }
        
        if (
          (text.includes('Draft') && !text.includes('Draft Report') && el.tagName !== 'DIV') ||
          (classNameStr && classNameStr.includes('draft-badge')) ||
          (classNameStr && classNameStr.includes('draft-tag')) ||
          (el.tagName === 'SPAN' && text.trim() === 'Draft')
        ) {
          el.remove()
        }
      })
      
      // Get cleaned HTML
      let reportHTML
      try {
        reportHTML = clonedElement.innerHTML || ''
        // Replace any instances of "reactBusinessPortal" with "AUMHUM" in the HTML
        reportHTML = reportHTML.replace(/reactBusinessPortal/gi, 'AUMHUM')
      } catch (htmlError) {
        throw new Error(`Failed to get report HTML: ${htmlError?.message || 'Unknown error'}`)
      }
      
      if (!reportHTML || reportHTML.trim().length === 0) {
        throw new Error('Report HTML is empty after processing')
      }
      
      // Get all inline styles from the current document
      let inlineStyles = ''
      try {
        inlineStyles = Array.from(document.querySelectorAll('style'))
          .map(style => style.innerHTML || '')
          .join('\n')
      } catch (styleError) {
        console.warn('Failed to extract inline styles:', styleError)
        // Continue without styles - not critical
      }
      
      // Create a new window for printing only the report content
      let printWindow
      try {
        printWindow = window.open('', '_blank', 'width=800,height=600')
      } catch (openError) {
        console.warn('window.open() failed, will use fallback method:', openError)
        printWindow = null
      }
      if (printWindow) {
        usedNewWindow = true
        
        // Generate filename format: <PatientNumber>_Therapy_Report_<startTime>
        // Calculate formattedTitle outside try block so it's accessible in onload handler
        const patientNumber = therapyReport?.patientNumber || 'Unknown'
        let startTimeFormatted = ''
        
        if (bookedEventInfo?.startTime) {
          startTimeFormatted = dayjs.utc(bookedEventInfo.startTime).local().format('YYYYMMDD_HHmmss')
        } else if (therapyReport?.createdTime) {
          startTimeFormatted = dayjs.utc(therapyReport.createdTime).local().format('YYYYMMDD_HHmmss')
        } else {
          startTimeFormatted = dayjs().format('YYYYMMDD_HHmmss')
        }
        
        // Format title: Replace _ with - and keep everything capitalized (all uppercase), no spaces
        const filenameParts = `${patientNumber}_Therapy_Report_${startTimeFormatted}`.split('_')
        const formattedTitle = filenameParts
          .map(part => {
            // If it's all numbers, keep as is. Otherwise convert to uppercase
            if (/^\d+$/.test(part)) {
              return part
            }
            return part.toUpperCase()
          })
          .join('-')
        
        try {
          // Get printed on/by information
          const printedOn = dayjs().format('DD/MM/YYYY HH:mm')
          const printedBy = therapyReport?.mentorName || 'System'
          
          printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>${formattedTitle}</title>
              <meta charset="utf-8">
              <meta name="application-name" content="AUMHUM">
              <meta property="og:site_name" content="AUMHUM">
              <style>
                ${inlineStyles}
                /* Reset all styles */
                * {
                  box-sizing: border-box;
                }
                html, body {
                  margin: 0 !important;
                  padding: 0 !important;
                  width: 100% !important;
                  height: auto !important;
                  min-height: auto !important;
                  max-height: none !important;
                  background: white !important;
                  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                  overflow: visible !important;
                }
                body {
                  overflow-x: visible !important;
                  overflow-y: visible !important;
                }
                /* Hide scrollbars completely */
                ::-webkit-scrollbar {
                  display: none !important;
                  width: 0 !important;
                  height: 0 !important;
                }
                * {
                  scrollbar-width: none !important;
                  -ms-overflow-style: none !important;
                }
                /* Remove any height constraints */
                div, section, article, main {
                  max-height: none !important;
                  height: auto !important;
                  min-height: auto !important;
                  overflow: visible !important;
                }
                /* Ensure content can flow across pages */
                * {
                  page-break-inside: auto !important;
                }
                /* Page settings */
                @page {
                  size: A4;
                  margin: 20mm;
                  margin-bottom: 35mm;
                }
                @media print {
                  @page {
                    size: A4;
                    margin: 20mm;
                    margin-bottom: 35mm;
                    @bottom-left {
                      content: "Printed on: ${printedOn} | Printed by: ${printedBy}";
                      font-size: 10px;
                      color: #64748b;
                      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
                      font-weight: 400;
                    }
                    @bottom-center {
                      content: "Page " counter(page) " of " counter(pages);
                      font-size: 11px;
                      color: #64748b;
                      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
                      font-weight: 500;
                    }
                  }
                  html, body {
                    margin: 0 !important;
                    padding: 0 !important;
                    width: 100% !important;
                    height: auto !important;
                    min-height: auto !important;
                    max-height: none !important;
                    background: white !important;
                    overflow: visible !important;
                  }
                  /* Hide scrollbars in print */
                  ::-webkit-scrollbar {
                    display: none !important;
                    width: 0 !important;
                    height: 0 !important;
                  }
                  * {
                    scrollbar-width: none !important;
                    -ms-overflow-style: none !important;
                    overflow: visible !important;
                  }
                  /* Remove ALL height constraints in print */
                  div, section, article, main, p, span, div > * {
                    max-height: none !important;
                    height: auto !important;
                    min-height: auto !important;
                    overflow: visible !important;
                    page-break-inside: auto !important;
                  }
                  /* Hide all UI elements */
                  .hide-on-print-only,
                  .therapy-report-draft-watermark,
                  .therapy-report-page-footer, /* Hide floating page footer - we use @page CSS instead */
                  button,
                  [class*="MuiButton"],
                  [class*="MuiIconButton"],
                  [class*="MuiDrawer"],
                  [class*="MuiBackdrop"],
                  [class*="MuiDialog"],
                  [class*="MuiModal"],
                  [class*="MuiPaper-root"][class*="MuiDrawer"],
                  [class*="report-drawer-header"],
                  [class*="report-drawer-actions"],
                  [class*="MuiToolbar"],
                  [class*="MuiAppBar"],
                  input[type="button"],
                  input[type="submit"],
                  a[role="button"],
                  [data-testid*="button"] {
                    display: none !important;
                    visibility: hidden !important;
                    opacity: 0 !important;
                    height: 0 !important;
                    width: 0 !important;
                    overflow: hidden !important;
                  }
                  /* Remove fixed position elements that might cause issues */
                  [style*="position: fixed"],
                  [style*="position:fixed"],
                  [style*="position: fixed;"],
                  [style*="position:fixed;"] {
                    display: none !important;
                    visibility: hidden !important;
                    opacity: 0 !important;
                    height: 0 !important;
                    width: 0 !important;
                    overflow: hidden !important;
                  }
                  /* Specifically target page number indicators */
                  div[style*="position: fixed"][style*="bottom"],
                  div[style*="position:fixed"][style*="bottom"],
                  div[style*="position: fixed"][style*="transform"],
                  div[style*="position:fixed"][style*="transform"] {
                    display: none !important;
                    visibility: hidden !important;
                    opacity: 0 !important;
                  }
                  /* Allow natural page breaks - don't prevent them */
                  * {
                    page-break-inside: auto !important;
                    page-break-after: auto !important;
                    page-break-before: auto !important;
                  }
                  /* Avoid breaking within important sections (but allow if needed) */
                  h1, h2, h3, h4, h5, h6 {
                    page-break-after: avoid;
                    page-break-inside: avoid;
                  }
                  table {
                    page-break-inside: avoid;
                  }
                  /* Ensure images don't break pages unnecessarily */
                  img {
                    page-break-inside: avoid;
                    max-width: 100% !important;
                  }
                }
              </style>
            </head>
            <body style="overflow: visible !important; height: auto !important; max-height: none !important;">
              ${reportHTML}
            </body>
          </html>
        `)
          printWindow.document.close()
        } catch (writeError) {
          printWindow.close()
          throw new Error(`Failed to write print content: ${writeError?.message || 'Unknown error'}`)
        }
        
        // Wait for content to load and images to render
        printWindow.onload = () => {
          // Set the document title to formatted filename
          printWindow.document.title = formattedTitle
          
          // Ensure body allows full content flow
          const body = printWindow.document.body
          const html = printWindow.document.documentElement
          
          body.style.overflow = 'visible'
          body.style.height = 'auto'
          body.style.maxHeight = 'none'
          body.style.minHeight = 'auto'
          html.style.overflow = 'visible'
          html.style.height = 'auto'
          html.style.maxHeight = 'none'
          html.style.minHeight = 'auto'
          
          // Remove any height constraints from ALL elements
          const allElements = body.querySelectorAll('*')
          allElements.forEach(el => {
            // Remove height constraints
            if (el.style.maxHeight && el.style.maxHeight !== 'none') {
              el.style.maxHeight = 'none'
            }
            if (el.style.height && el.style.height !== 'auto' && !el.style.height.includes('%')) {
              el.style.height = 'auto'
            }
            if (el.style.minHeight && el.style.minHeight !== 'auto') {
              el.style.minHeight = 'auto'
            }
            // Ensure overflow is visible
            if (el.style.overflow && el.style.overflow !== 'visible') {
              el.style.overflow = 'visible'
            }
            // Remove fixed positioning that might cause issues
            if (el.style.position === 'fixed') {
              el.style.position = 'static'
            }
          })
          
          // Remove any floating page number indicators - be more aggressive
          const fixedElements = body.querySelectorAll('*')
          fixedElements.forEach(el => {
            const style = el.getAttribute('style') || ''
            const text = (el.textContent || '').trim().toLowerCase()
            
            // Check if it's fixed position
            const isFixed = style.includes('position: fixed') || 
                           style.includes('position:fixed') ||
                           style.includes('position: fixed;') ||
                           style.includes('position:fixed;')
            
            // Check for page-related text (including "Page.", "Page 1", etc.)
            const hasPageText = text.includes('page') || 
                              text.match(/page\s*\.?\s*\d*/) ||
                              text.match(/page\s*of/)
            
            // Remove if fixed and has page text
            if (isFixed && hasPageText) {
              el.remove()
            }
            // Also remove fixed elements at bottom with transform (centered page indicators)
            else if (isFixed && (style.includes('bottom:') || style.includes('transform:'))) {
              if (hasPageText || (style.includes('bottom:') && style.includes('transform:'))) {
                el.remove()
              }
            }
          })
          
          setTimeout(() => {
            printWindow.focus()
            printWindow.print()
            // Close window after print dialog is shown and reset state
            setTimeout(() => {
              printWindow.close()
              setIsPrintingPastHistoryReports(false)
              setIsPrinting(false)
            }, 1000)
          }, 500)
        }
        
        // Fallback if onload doesn't fire
        setTimeout(() => {
          if (printWindow.document.readyState === 'complete') {
            // Generate formatted title for fallback
            const patientNumber = therapyReport?.patientNumber || 'Unknown'
            let startTimeFormatted = ''
            
            if (bookedEventInfo?.startTime) {
              startTimeFormatted = dayjs.utc(bookedEventInfo.startTime).local().format('YYYYMMDD_HHmmss')
            } else if (therapyReport?.createdTime) {
              startTimeFormatted = dayjs.utc(therapyReport.createdTime).local().format('YYYYMMDD_HHmmss')
            } else {
              startTimeFormatted = dayjs().format('YYYYMMDD_HHmmss')
            }
            
            const filenameParts = `${patientNumber}_Therapy_Report_${startTimeFormatted}`.split('_')
            const formattedTitleFallback = filenameParts
              .map(part => {
                // If it's all numbers, keep as is. Otherwise convert to uppercase
                if (/^\d+$/.test(part)) {
                  return part
                }
                return part.toUpperCase()
              })
              .join('-')
            
            // Set the document title to formatted filename
            printWindow.document.title = formattedTitleFallback
            
            const body = printWindow.document.body
            const html = printWindow.document.documentElement
            
            body.style.overflow = 'visible'
            body.style.height = 'auto'
            body.style.maxHeight = 'none'
            body.style.minHeight = 'auto'
            html.style.overflow = 'visible'
            html.style.height = 'auto'
            html.style.maxHeight = 'none'
            html.style.minHeight = 'auto'
            
            // Remove height constraints from all elements
            const allElements = body.querySelectorAll('*')
            allElements.forEach(el => {
              if (el.style.maxHeight && el.style.maxHeight !== 'none') {
                el.style.maxHeight = 'none'
              }
              if (el.style.height && el.style.height !== 'auto' && !el.style.height.includes('%')) {
                el.style.height = 'auto'
              }
              if (el.style.overflow && el.style.overflow !== 'visible') {
                el.style.overflow = 'visible'
              }
              if (el.style.position === 'fixed') {
                el.style.position = 'static'
              }
            })
            
            // Remove floating page number indicators - be more aggressive
            const fixedElements = body.querySelectorAll('*')
            fixedElements.forEach(el => {
              const style = el.getAttribute('style') || ''
              const text = (el.textContent || '').trim().toLowerCase()
              
              // Check if it's fixed position
              const isFixed = style.includes('position: fixed') || 
                             style.includes('position:fixed') ||
                             style.includes('position: fixed;') ||
                             style.includes('position:fixed;')
              
              // Check for page-related text (including "Page.", "Page 1", etc.)
              const hasPageText = text.includes('page') || 
                                text.match(/page\s*\.?\s*\d*/) ||
                                text.match(/page\s*of/)
              
              // Remove if fixed and has page text
              if (isFixed && hasPageText) {
                el.remove()
              }
              // Also remove fixed elements at bottom with transform (centered page indicators)
              else if (isFixed && (style.includes('bottom:') || style.includes('transform:'))) {
                if (hasPageText || (style.includes('bottom:') && style.includes('transform:'))) {
                  el.remove()
                }
              }
            })
            
            printWindow.focus()
            printWindow.print()
            setTimeout(() => {
              printWindow.close()
              setIsPrintingPastHistoryReports(false)
              setIsPrinting(false)
            }, 1000)
          } else {
            // If window didn't load, reset state anyway
            setIsPrintingPastHistoryReports(false)
            setIsPrinting(false)
          }
        }, 2000)
      } else {
        // Fallback: Use CSS visibility trick to hide everything except report
        try {
          const printStyle = document.createElement('style')
          printStyle.id = 'therapy-report-print-style'
          printStyle.textContent = `
          @media print {
            /* Hide scrollbars */
            ::-webkit-scrollbar {
              display: none !important;
              width: 0 !important;
              height: 0 !important;
            }
            * {
              scrollbar-width: none !important;
              -ms-overflow-style: none !important;
            }
            html, body {
              overflow: visible !important;
              height: auto !important;
            }
            /* Hide everything by default */
            body * {
              visibility: hidden !important;
            }
            /* Show only the print body rectangle */
            .therapy-report-print-body,
            .therapy-report-print-body *,
            .therapy-report-print-content,
            .therapy-report-print-content * {
              visibility: visible !important;
            }
            .therapy-report-print-body,
            .therapy-report-print-content {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            /* Hide drawer and UI elements */
            [class*="MuiBackdrop"],
            [class*="MuiDialog"],
            [class*="MuiModal"],
            [class*="report-drawer-header"],
            [class*="report-drawer-actions"],
            [class*="DrawerHeader"],
            [class*="DrawerTitle"],
            [class*="ActionButtonsContainer"],
            .hide-on-print-only,
            .therapy-report-page-footer, /* Hide floating page footer - we use @page CSS instead */
            button,
            [class*="MuiButton"],
            [class*="MuiIconButton"],
            input[type="button"],
            input[type="submit"],
            a[role="button"] {
              display: none !important;
              visibility: hidden !important;
              opacity: 0 !important;
            }
            @page {
              size: A4;
              margin: 20mm;
              margin-bottom: 35mm;
              @bottom-center {
                content: "Page " counter(page) " of " counter(pages);
                font-size: 11px;
                color: #64748b;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
                font-weight: 500;
              }
            }
            /* Ensure content flows across pages */
            * {
              page-break-inside: avoid;
            }
          }
        `
        document.head.appendChild(printStyle)
        
        // Add class to report content (for backward compatibility)
        reportElement.classList.add('therapy-report-print-content')
        
        // Also ensure the print body class is present
        if (!reportElement.classList.contains('therapy-report-print-body')) {
          reportElement.classList.add('therapy-report-print-body')
        }
        
        // Ensure body allows scrolling
        const originalBodyOverflow = document.body.style.overflow
        const originalHtmlOverflow = document.documentElement.style.overflow
        document.body.style.overflow = 'visible'
        document.documentElement.style.overflow = 'visible'
        
        // Use beforeprint and afterprint events to handle state reset properly
        const handleAfterPrint = () => {
          // Cleanup after print dialog closes
          reportElement.classList.remove('therapy-report-print-content')
          const styleEl = document.getElementById('therapy-report-print-style')
          if (styleEl) {
            document.head.removeChild(styleEl)
          }
          document.body.style.overflow = originalBodyOverflow
          document.documentElement.style.overflow = originalHtmlOverflow
          // Reset printing state after print dialog closes
          setIsPrintingPastHistoryReports(false)
          setIsPrinting(false)
          // Remove event listeners
          window.removeEventListener('afterprint', handleAfterPrint)
        }
        
        // Add event listener for afterprint
        window.addEventListener('afterprint', handleAfterPrint)
        
        // Trigger print
        window.print()
        
          // Fallback: If afterprint doesn't fire (some browsers), reset after timeout
          // This is a safety net in case the afterprint event doesn't fire
          setTimeout(() => {
            // Check if cleanup hasn't happened yet (style element still exists)
            const styleEl = document.getElementById('therapy-report-print-style')
            if (styleEl) {
              handleAfterPrint()
            }
          }, 2000)
          
          // Don't reset in finally block for fallback method - let afterprint handle it
          return
        } catch (fallbackError) {
          // If fallback method fails, clean up and throw error
          const styleEl = document.getElementById('therapy-report-print-style')
          if (styleEl) {
            try {
              document.head.removeChild(styleEl)
            } catch (e) {
              // Ignore cleanup errors
            }
          }
          if (reportElement) {
            try {
              reportElement.classList.remove('therapy-report-print-content')
            } catch (e) {
              // Ignore cleanup errors
            }
          }
          throw new Error(`Fallback print method failed: ${fallbackError?.message || 'Unknown error'}`)
        }
      }
    } catch (error) {
      console.error('Error during print:', error)
      console.error('Error stack:', error?.stack)
      console.error('Error message:', error?.message)
      // More user-friendly error message
      const errorMessage = error?.message || 'Unknown error occurred'
      alert(`An error occurred while trying to print: ${errorMessage}. Please try again.`)
      setIsPrintingPastHistoryReports(false)
      setIsPrinting(false)
    } finally {
      // Only reset if we used the new window method (not fallback)
      // For fallback, afterprint event handles the reset
      if (!usedNewWindow) {
        // State reset is handled by afterprint event for fallback method
        return
      }
      // For new window method, state reset is handled in the printWindow callbacks
    }
  }

  async function issueAndPrint() {
    if (!onIssueReport) {
      console.error('onIssueReport function not provided')
      alert('Unable to issue report: Save function not available. Please save the report manually.')
      return
    }
    
    setIssuingReport(true)
    try {
      // Save with status=completed
      await onIssueReport()
      // Wait a bit for the report to be updated
      await sleep(500)
      // Now print
      await printTherapy()
    } catch (error) {
      console.error('Error issuing report:', error)
      alert('Failed to issue report. Please try again.')
    } finally {
      setIssuingReport(false)
    }
  }

  async function saveAndGeneratePDF(type) {
    setSaving(true)
    await afterSaveGeneratePdfFunc()
    
    // Format filename as: <PatientNumber>_Therapy_Report_<startTime>
    const patientNumber = therapyReport?.patientNumber || 'Unknown'
    let startTimeFormatted = ''
    
    if (bookedEventInfo?.startTime) {
      // Format as YYYYMMDD_HHmmss for filename (no spaces or special chars)
      startTimeFormatted = dayjs.utc(bookedEventInfo.startTime).local().format('YYYYMMDD_HHmmss')
    } else if (therapyReport?.createdTime) {
      // Fallback to createdTime if bookedEventInfo not available
      startTimeFormatted = dayjs.utc(therapyReport.createdTime).local().format('YYYYMMDD_HHmmss')
    } else {
      // Final fallback to current time
      startTimeFormatted = dayjs().format('YYYYMMDD_HHmmss')
    }
    
    const filename = `${patientNumber}_Therapy_Report_${startTimeFormatted}.pdf`
    
    if (type === 'open') {
      await generatePDF(therapyReportContainerRef, {
        filename: filename,
        method: 'open',
        page: { margin: Margin.MEDIUM },
      })
    } else {
      await generatePDF(therapyReportContainerRef, {
        filename: filename,
        method: 'save',
        page: { margin: Margin.MEDIUM },
      })
    }
    setSaving(false)
  }

  async function sendReportToTriggeringPractitioner() {
    if (!sendReportToTriggeringPractitionerApiSignature) {
      console.error('API signature not provided')
      return
    }

    try {
      setSendingReport(true)
      
      // Detect if this is a support portal API (contains /api/secure/cc/)
      const isSupportPortal = sendReportToTriggeringPractitionerApiSignature.includes('/api/secure/cc/')
      
      if (isSupportPortal) {
        // Support portal: reportType is in the path, only need eventId in query
        const params = {
          eventId: therapyReport?.bookedEventId,
        }
        const response = await postSPDataWithParams(
          sendReportToTriggeringPractitionerApiSignature,
          null,
          params,
        )
        return response
      } else {
        // Mentor portal: Use unified endpoint with reportType in query params
        // Query params: reportType (required), eventId OR reportId
        const params = {
          reportType: 'therapy-report',
          eventId: therapyReport?.bookedEventId,
        }
        const response = await postMPDataWithParams(
          sendReportToTriggeringPractitionerApiSignature,
          null,
          params,
        )
        return response
      }
    } catch (error) {
      console.error('Failed to send report:', error)
      throw error
    } finally {
      setSendingReport(false)
    }
  }

  async function downloadTherapyReport() {
    if (!therapyReport?.bookedEventId) {
      console.error('Booked event ID not available')
      toast.error('Unable to download: Missing event ID')
      return
    }

    try {
      setDownloadingReport(true)
      
      const token = localStorage.getItem('token')
      const countryCode = getCountryCode()
      const mainLanguageCode = getMainLanguageCode()
      
      // Detect portal based on API signature if available, otherwise default to mentor
      const isSupportPortal = sendReportToTriggeringPractitionerApiSignature?.includes('/api/secure/cc/') || false
      
      const baseURL = isSupportPortal ? supportApiURL : mentorApiURL
      const endpoint = isSupportPortal 
        ? '/api/secure/cc/support/practitioner/emr-reports/download'
        : '/api/secure/mentor/emr-reports/download'
      
      const response = await axios.get(`${baseURL}${endpoint}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'ef-user-country-code': countryCode,
          'ef-app-language': mainLanguageCode,
        },
        params: {
          reportType: 'therapy-report',
          eventId: therapyReport.bookedEventId,
          languageCode: mainLanguageCode,
          platform: 'web',
          countryCode: countryCode,
        },
        responseType: 'blob', // Important for PDF download
      })
      
      if (response?.status === 200 && response.data) {
        // Get filename from Content-Disposition header
        const contentDisposition = response.headers?.['content-disposition']
        let filename = 'therapy-report.pdf'
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '')
          }
        }
        
        // Create blob and download
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        
        // Cleanup
        window.URL.revokeObjectURL(url)
        document.body.removeChild(link)
        
        toast.success('Report downloaded successfully')
      } else {
        toast.error('Failed to download report')
      }
    } catch (error) {
      console.error('Failed to download report:', error)
      toast.error('Failed to download report. Please try again.')
    } finally {
      setDownloadingReport(false)
    }
  }

  return {
    printTherapy,
    isPrinting,
    isPrintingActive: isPrintingVar, // Actual printing state (not affected by isAlwaysPrinting)
    saving,
    saveAndGeneratePDF,
    sendReportToTriggeringPractitioner,
    sendingReport,
    issueAndPrint,
    issuingReport,
    downloadTherapyReport,
    downloadingReport,
  }
}
